import React, { useState, useEffect } from "react";
import { TextInput, Button, Checkbox, Icon, Breadcrumb } from "pepsico-ds";
import { useForm, useWatch } from "react-hook-form";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import axios from "axios";
import forge from "node-forge";
import { ToastContainer, toast } from "react-toastify";

const publicKey = `-----BEGIN RSA PUBLIC KEY-----
MIIBIjANBgkqhkiG9w0BAQEFAAOCAQ8AMIIBCgKCAQEAsoZnlKyNcmMo3zrdx3KGc+/+fk1Jn7zYVyENg8hf6+SQ+p03Mzx8+hO6QUWgkJtlQfWBabZWMolGcpr37QTs4LJc6IEdvy24b8SjWM8AXj6II+SRpOR1m7W9I0eD16+6/nLWBpB5FAxCjrODC//BZVvGStXnTfQZc+/E2EamK21DneFkJaC9FlTwoNSy+LM5bjf6Q2YjWuX06uePzaAOLnycyhA8fHMoHwBQLdzfpKwRKMOr3VnDilem9RH9zGi44rg8y2g2TY1WzvsStmDM8NduV5aOnwzFMo+BTugfLhdP4ZmYR/BX5WLfPmxkAEI5fVNONBCuzoJsyX424wMLlwIDAQAB
-----END RSA PUBLIC KEY-----`;

const encrypt = (plainText, key) => {
  const rsaPublicKey = forge.pki.publicKeyFromPem(key);

  return forge.util.encode64(
    rsaPublicKey.encrypt(plainText, "RSA-OAEP", {
      md: forge.md.md5.create(),
      mgf1: {
        md: forge.md.sha1.create(),
      },
    }),
  );
};

const IdxPasswordReset = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [passwordUpdated, setPasswordUpdated] = useState(false);
  const { REACT_APP_API_ENDPOINT } = process.env;
  const [type, setType] = useState("password");
  const [reType, setReType] = useState("password");

  const [icon, setIcon] = useState("visibility");
  const [reTypeIcon, setReTypeIcon] = useState("visibility");

  const [isConfirmed, setIsConfirmed] = useState(false);

  const [passwordValue, setPasswordValue] = useState("");
  const [showGuidelines, setShowGuidelines] = useState(false);
  const [isTyped, setIsTyped] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [userId, setUserId] = useState("");
  const [hasPBC, setHasPBC] = useState(false);
  const PASSWORD_POLICY = hasPBC
  ? { min: 8, max: 14 }
  : { min: 12, max: 24 };


  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);
  useEffect(() => {
    const data = JSON.parse(localStorage.getItem("okta-token-storage"));

    const loggedInGPID = data?.accessToken?.claims?.gpid || "";

    setUserId(loggedInGPID);
  }, []);

  useEffect(() => {
    if (userId) {
      getPasswordPolicy(userId);
    }
  }, [userId]);
  
  useEffect(() => {
  console.log("UPDATED hasPBC =", hasPBC);
}, [hasPBC]);

  const [powdHints, setPowdHints] = useState({
    minLength: false,
    maxLength: false,
    hasLowercase: false,
    hasUppercase: false,
    hasSpecialChar: false,
    hasTwoNumbers: false,
    hasFourLetters: false,
    noSequentialChars: true,
    noForbiddenKeywords: true,
    noRepeatingChars: true,
    notStartWithNumber: true,
    notEndWithNumber: true,
    noSpecialChars: false,
  });

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm({
    mode: "onChange",
  });
  const passwordField = register("password");
  const getHintColor = (conditionMet) => {
    return isTyped ? (conditionMet ? "green" : "red") : "#9e9e9e";
  };

  const password = useWatch({
    control,
    name: "password",
  });

  const confirmPassword = useWatch({
    control,
    name: "confirmPassword",
  });

  const handleToggle = () => {
    if (type === "password") {
      setType("text");
      setIcon("visibility_off");
    } else {
      setType("password");
      setIcon("visibility");
    }
  };

  const handleToggleRetypePassword = () => {
    if (reType === "password") {
      setReType("text");
      setReTypeIcon("visibility_off");
    } else {
      setReType("password");
      setReTypeIcon("visibility");
    }
  };

  const forbiddenKeywords = [
    "spring",
    "summer",
    "fall",
    "winter",
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
    "Pepsi",
    "PepsiCo",
  ];

  const isSequential = (str) => {
    for (let i = 0; i < str.length - 2; i++) {
      if (
        str.charCodeAt(i + 1) === str.charCodeAt(i) + 1 &&
        str.charCodeAt(i + 2) === str.charCodeAt(i) + 2
      ) {
        return true;
      }
    }
    return false;
  };

  const validatePassword = (value) => {
    if (!value) return false;

   const minLength = value.length >= PASSWORD_POLICY.min;
   const maxLength = value.length <= PASSWORD_POLICY.max;

    const hasLowercase = /[a-z]/.test(value);
    const hasUppercase = /[A-Z]/.test(value);

    const hasSpecialChar = /[!^*()_|~`{}.#\[\]:;'"<>?,]/.test(value);

    const hasTwoNumbers = (value.match(/\d/g) || []).length >= 2;

    const hasFourLetters = (value.match(/[a-zA-Z]/g) || []).length >= 4;

    const noRepeatingChars = !/(.)\1{2,}/i.test(value);

    const notStartWithNumber = !/^\d/.test(value);

    const notEndWithNumber = !/\d$/.test(value);

    const noSequentialChars = !isSequential(value);

    const noForbiddenKeywords = !forbiddenKeywords.some((keyword) =>
      value.toLowerCase().includes(keyword.toLowerCase()),
    );
    const noSpecialChars = /^[a-zA-Z0-9]*$/.test(value);
    return (
      minLength &&
      maxLength &&
      hasLowercase &&
      hasUppercase &&
      (hasPBC ? noSpecialChars : hasSpecialChar) &&
      hasTwoNumbers &&
      hasFourLetters &&
      noRepeatingChars &&
      notStartWithNumber &&
      notEndWithNumber &&
      noSequentialChars &&
      noForbiddenKeywords
    );
  };

  const handlePasswordChange = (value) => {
    setIsTyped(true);
    setPasswordValue(value);
    setShowGuidelines(!!value);

    setPowdHints({
     minLength: value.length >= PASSWORD_POLICY.min,
     maxLength: value.length <= PASSWORD_POLICY.max,
      hasLowercase: /[a-z]/.test(value),
      hasUppercase: /[A-Z]/.test(value),
      hasSpecialChar: /[!^*()_|~`{}.#\[\]:;'"<>?,]/.test(value),
      hasTwoNumbers: (value.match(/\d/g) || []).length >= 2,
      hasFourLetters: (value.match(/[a-zA-Z]/g) || []).length >= 4,
      noRepeatingChars: !/(.)\1{2,}/i.test(value),
      notStartWithNumber: !/^\d/.test(value),
      notEndWithNumber: !/\d$/.test(value),
      noSequentialChars: !isSequential(value),
      noForbiddenKeywords: !forbiddenKeywords.some((keyword) =>
        value.toLowerCase().includes(keyword.toLowerCase()),
      ),
      noSpecialChars: /^[a-zA-Z0-9]*$/.test(value),
    });
  };

  const getStrength = () => {
    if (!passwordValue) {
      return {
        label: "",
        width: 0,
        color: "#DADADA",
      };
    }

    let score = 0;

    if (passwordValue.length >= PASSWORD_POLICY.min) score++;
    if (/[a-z]/.test(passwordValue)) score++;
    if (/[A-Z]/.test(passwordValue)) score++;
    if ((passwordValue.match(/\d/g) || []).length >= 2) score++;
    if (hasPBC) {
      if (/^[a-zA-Z0-9]*$/.test(passwordValue)) {
        score++;
      }
    } else {
      if (/[!@#$%^&*()_\-+=<>?/{}[\]|:;"'`~]/.test(passwordValue)) {
        score++;
      }
    }

    if (score >= 5) {
      return {
        label: t("changePassword.strong"),
        width: 100,
        color: "#027C06",
      };
    }

    if (score >= 3) {
      return {
        label: t("changePassword.moderate"),
        width: 70,
        color: "#D6AC16",
      };
    }

    return {
      label: t("changePassword.weak"),
      width: 40,
      color: "#C12C01",
    };
  };

  const strength = getStrength();

  const isPasswordValid = validatePassword(passwordValue);

  const isReady =
    isPasswordValid &&
    password === confirmPassword &&
    confirmPassword &&
    isConfirmed;

  const onSubmit = async () => {
    if (isSubmitting) return;

    setIsSubmitting(true);

    try {
      const encryptedPassword = encrypt(password, publicKey);

      const payload = {
        gpid: userId,
        password: encryptedPassword,
        confirmPassword: encryptedPassword,
        termCondition: "true",
      };

      const response = await axios.post(
        REACT_APP_API_ENDPOINT + "user/idxPasswordReset",
        payload,
      );

      if (response?.data?.status === "Success") {
        setPasswordUpdated(true);
      } else {
        console.error("API Failed:", response.data);

        toast.error(response?.data?.errorMessage || "Password reset failed");
      }
    } catch (error) {
      console.error("IDX Password Reset Error:", error);

      toast.error(
        error?.response?.data?.errorMessage || "Unexpected error occurred",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const getPasswordPolicy = async (gpid) => {
    try {
      const response = await axios.post(
        REACT_APP_API_ENDPOINT + "/user/getEndpointAccount",
        {
          gpid,
        },
      );
     
console.log(
  "Policy =",
  response?.data?.pepPasswordPolicy
);

     const isException =
  (response?.data?.pepPasswordPolicy || "")
    .trim()
    .toLowerCase() === "exception";

console.log("isException =", isException);

setHasPBC(isException);
    } catch (error) {
      console.error("Failed to get password policy", error);
    }
  };

  const hasMustHints =
    getHintColor(
      powdHints.minLength &&
        powdHints.maxLength &&
        powdHints.hasTwoNumbers &&
        powdHints.hasFourLetters,
    ) !== "green" ||
    getHintColor(powdHints.hasLowercase) !== "green" ||
    getHintColor(powdHints.hasUppercase) !== "green" ||
    (!hasPBC && getHintColor(powdHints.hasSpecialChar) !== "green");

  const hasMustNotHints =
    getHintColor(powdHints.noRepeatingChars) !== "green" ||
    getHintColor(powdHints.notStartWithNumber) !== "green" ||
    getHintColor(powdHints.notEndWithNumber) !== "green" ||
    getHintColor(powdHints.noSequentialChars) !== "green" ||
    (hasPBC && getHintColor(powdHints.noSpecialChars) !== "green") ||
    getHintColor(powdHints.noForbiddenKeywords) !== "green";

  if (passwordUpdated) {
    return (
      <div className="idx-password-success-page">
        <div className="idx-password-header">
          <Icon icon="vpn_key" size="small" />

          <div className="idx-password-title">
            IDX Pre-Production (ITE/QA) Password Reset
          </div>
        </div>

        <div className="idx-password-success-card">
          <div className="idx-password-success-icon">
            <div className="idx-password-circle">
              <Icon icon="vpn_key" size="large" />
            </div>

            <div className="idx-password-check">
              <Icon icon="check" size="small" />
            </div>
          </div>

          <div className="idx-password-success-title">
            {t("changePassword.password_update_success")}
          </div>

          <div className="idx-password-success-text">
            {t("changePassword.success_update_text_accounts_idx")}
          </div>

          <div className="idx-password-success-footer">
            <Button iconLeading="arrow_back" onClick={() => navigate("/home")}>
              {t("changePassword.back_to_home")}
            </Button>
          </div>
        </div>
      </div>
    );
  }
  console.log("password =", password);
  console.log("confirmPassword =", confirmPassword);
  console.log("RENDER hasPBC =", hasPBC);

  return (
    <>
     <div style={{marginLeft:"20px"}}
        className={`${
          isMobile ? "pl-4 pr-4 ml-4 mr-4" : ""
        } ad-main-heading-container`}
      >
        <Breadcrumb
          homeIcon={{
              icon: "home",
              onClick:()=>{
              navigate("/home")
              }
          }}
          breadcrumbs={[
            {
              label: "Home",
            },
            {
            label: t("idx.idx_password_reset")
            },
          ]}
          className="idx-breadcrumb"
          useAnchor
        />
        <div className="ad-page-header">
          <div className="idx-password-header">
            {/* <Icon icon="vpn_key" size="small" /> */}

            <div>
              <div className="ad-main-heading">
                {t("idx.idx_preprod_password_reset")} {t("idx.password_reset")}
              </div>

              {/* <div className="ad-main-heading">
     
        </div> */}
            </div>
          </div>
        </div>
      </div>

      <div className="idx-password-reset-wrapper">
        <div className="card idx-password-reset-card">
          <div className="card-content">
            <h5>{t("changePassword.create_new_password")}</h5>

            <form className="form-size" onSubmit={handleSubmit(onSubmit)}>
              <TextInput disabled label={t("idx.user_id")} value={userId} />

              <div className="change-new-password-input">
                <TextInput
                  type={type}
                  maxLength={PASSWORD_POLICY.max}
                  required
                  label={t("changePassword.new_password")}
                  placeholderText={t("changePassword.create_strong_password")}
                  {...passwordField}
                  onChange={(e) => {
                    passwordField.onChange(e);
                    handlePasswordChange(e.target.value);
                  }}
                />

                <div className="hide-icon idx-hide-icon" onClick={handleToggle}>
                  <Icon icon={icon} size="18px" />
                </div>
              </div>
              {passwordValue && (
                <div className="idx-strength-section">
                  <div className="strength-label">{t("idx.strength")}</div>

                  <div className="strength-bar">
                    <div
                      className="strength-fill"
                      style={{
                        width: `${strength.width}%`,
                        backgroundColor: strength.color,
                      }}
                    />
                  </div>

                  <div className="idx-strength-text">{strength.label}</div>
                </div>
              )}

              <div className="change-new-password-input">
                <TextInput
                  type={reType}
                  maxLength={PASSWORD_POLICY.max}
                  required
                  label={t("changePassword.retype_password")}
                  placeholderText={t("changePassword.retype_strong_password")}
                  {...register("confirmPassword", {
                    validate: (value) =>
                      value === password ||
                      t("changePassword.password_not_match"),
                  })}
                />

                <div
                  className="hide-icon idx-hide-icon"
                  onClick={handleToggleRetypePassword}
                >
                  <Icon icon={reTypeIcon} size="18px" />
                </div>
              </div>
              {showGuidelines && !isPasswordValid && (
                <div className="passwordGuidelines">
                  <div className="passGuides">
                    {(hasMustHints || hasMustNotHints) && (
                      <h5 style={{ fontSize: "1rem" }}>
                        {t("changePassword.password_guidelines")}
                      </h5>
                    )}

                    {hasMustHints && (
                      <h5 style={{ fontSize: "1rem" }} className="mb-0 d-flex">
                        {t("changePassword.must")}:
                      </h5>
                    )}
                    {getHintColor(
                      powdHints.minLength &&
                        powdHints.maxLength &&
                        powdHints.hasTwoNumbers &&
                        powdHints.hasFourLetters,
                    ) !== "green" && (
                      <li
                        className="hintText no-select"
                        style={{
                          color: getHintColor(
                            powdHints.minLength &&
                              powdHints.maxLength &&
                              powdHints.hasTwoNumbers &&
                              powdHints.hasFourLetters,
                          ),
                        }}
                        onCopy={(e) => e.preventDefault()}
                      >
                        <Icon
                          alt="alt value"
                          icon={
                            getHintColor(
                              powdHints.minLength &&
                                powdHints.maxLength &&
                                powdHints.hasTwoNumbers &&
                                powdHints.hasFourLetters,
                            ) === "red"
                              ? "close"
                              : "check_circle"
                          }
                          size="small"
                          style={{ alignItems: "center" }}
                        />
                        {t("changePassword.hint_text1", {
                         minLength: PASSWORD_POLICY.min,
                         maxLength: PASSWORD_POLICY.max,
                        })}
                      </li>
                    )}
                    {getHintColor(powdHints.hasLowercase) !== "green" && (
                      <li
                        className="hintText no-select"
                        style={{ color: getHintColor(powdHints.hasLowercase) }}
                        onCopy={(e) => e.preventDefault()}
                      >
                        <Icon
                          alt="alt value"
                          icon={
                            getHintColor(powdHints.hasLowercase) === "red"
                              ? "close"
                              : "check_circle"
                          }
                          size="small"
                          style={{ alignItems: "center" }}
                        />
                        {t("changePassword.hint_text_2")}
                      </li>
                    )}
                    {getHintColor(powdHints.hasUppercase) !== "green" && (
                      <li
                        className="hintText no-select"
                        style={{ color: getHintColor(powdHints.hasUppercase) }}
                        onCopy={(e) => e.preventDefault()}
                      >
                        <Icon
                          alt="alt value"
                          icon={
                            getHintColor(powdHints.hasUppercase) === "red"
                              ? "close"
                              : "check_circle"
                          }
                          size="small"
                          style={{ alignItems: "center" }}
                        />
                        {t("changePassword.hint_text_3")}
                      </li>
                    )}
                    {!hasPBC &&
                      getHintColor(powdHints.hasSpecialChar) !== "green" && (
                        <li
                          className="hintText no-select"
                          style={{
                            color: getHintColor(powdHints.hasSpecialChar),
                          }}
                          onCopy={(e) => e.preventDefault()}
                        >
                          <Icon
                            alt="alt value"
                            icon={
                              getHintColor(powdHints.hasSpecialChar) === "red"
                                ? "close"
                                : "check_circle"
                            }
                            size="small"
                            style={{ alignItems: "center" }}
                          />
                          {t("changePassword.hint_text_4")} !^*()_|~`{"{}"}
                          .#[]:";'&lt; &gt;?,
                        </li>
                      )}

                    {hasPBC &&
                      getHintColor(powdHints.noSpecialChars) !== "green" && (
                        <li
                          className="hintText no-select"
                          style={{
                            color: getHintColor(powdHints.noSpecialChars),
                          }}
                          onCopy={(e) => e.preventDefault()}
                        >
                          <Icon
                            alt="alt value"
                            icon={
                              getHintColor(powdHints.noSpecialChars) === "red"
                                ? "close"
                                : "check_circle"
                            }
                            size="small"
                            style={{ alignItems: "center" }}
                          />
                          {t("changePassword.hint_text_pbc")}
                        </li>
                      )}
                    {powdHints && hasMustNotHints && (
                      <h5
                        style={{ fontSize: "1rem" }}
                        className="mb-1 mt-1 d-flex"
                      >
                        {t("changePassword.must_not")}
                      </h5>
                    )}

                    {getHintColor(powdHints.noRepeatingChars) !== "green" && (
                      <li
                        className="hintText no-select"
                        style={{
                          color: getHintColor(powdHints.noRepeatingChars),
                        }}
                        onCopy={(e) => e.preventDefault()}
                      >
                        <Icon
                          alt="alt value"
                          icon={
                            getHintColor(powdHints.noRepeatingChars) === "red"
                              ? "close"
                              : "check_circle"
                          }
                          size="small"
                          style={{ alignItems: "center" }}
                        />
                        {t("changePassword.hint_text_6")}
                      </li>
                    )}

                    {getHintColor(powdHints.notStartWithNumber) !== "green" && (
                      <li
                        className="hintText no-select"
                        style={{
                          color: getHintColor(powdHints.notStartWithNumber),
                        }}
                        onCopy={(e) => e.preventDefault()}
                      >
                        <Icon
                          alt="alt value"
                          icon={
                            getHintColor(powdHints.notStartWithNumber) === "red"
                              ? "close"
                              : "check_circle"
                          }
                          size="small"
                          style={{ alignItems: "center" }}
                        />
                        {t("changePassword.hint_text_8")}
                      </li>
                    )}
                    {getHintColor(powdHints.notEndWithNumber) !== "green" && (
                      <li
                        className="hintText no-select"
                        style={{
                          color: getHintColor(powdHints.notEndWithNumber),
                        }}
                        onCopy={(e) => e.preventDefault()}
                      >
                        <Icon
                          alt="alt value"
                          icon={
                            getHintColor(powdHints.notEndWithNumber) === "red"
                              ? "close"
                              : "check_circle"
                          }
                          size="small"
                          style={{ alignItems: "center" }}
                        />
                        {t("changePassword.hint_text_9")}
                      </li>
                    )}
                    {getHintColor(powdHints.noSequentialChars) !== "green" && (
                      <li
                        className="hintText no-select"
                        style={{
                          color: getHintColor(powdHints.noSequentialChars),
                        }}
                        onCopy={(e) => e.preventDefault()}
                      >
                        <Icon
                          alt="alt value"
                          icon={
                            getHintColor(powdHints.noSequentialChars) === "red"
                              ? "close"
                              : "check_circle"
                          }
                          size="small"
                          style={{ alignItems: "center" }}
                        />
                        {t("changePassword.hint_text10")}
                      </li>
                    )}

                    {getHintColor(powdHints.noForbiddenKeywords) !==
                      "green" && (
                      <li
                        className="hintText no-select mb-2"
                        style={{
                          color: getHintColor(powdHints.noForbiddenKeywords),
                        }}
                        onCopy={(e) => e.preventDefault()}
                      >
                        <Icon
                          alt="alt value"
                          icon={
                            getHintColor(powdHints.noForbiddenKeywords) ===
                            "red"
                              ? "close"
                              : "check_circle"
                          }
                          size="small"
                          style={{ alignItems: "center" }}
                        />
                        {t("changePassword.hint_text12")}
                      </li>
                    )}

                    {(getHintColor(powdHints.noRepeatingChars) !== "green" ||
                      getHintColor(powdHints.notStartWithNumber) !== "green" ||
                      getHintColor(powdHints.notEndWithNumber) !== "green" ||
                      getHintColor(powdHints.noSequentialChars) !== "green" ||
                      (hasPBC &&
                        getHintColor(powdHints.noSpecialChars) !== "green") ||
                      getHintColor(powdHints.noForbiddenKeywords) !==
                        "green") && (
                      <li
                        className="hintText no-select d-flex"
                        onCopy={(e) => e.preventDefault()}
                      >
                        <Icon
                          alt="alt value"
                          icon="info_outline"
                          size="small"
                          style={{ width: "15px" }}
                        />
                        {t("changePassword.hint_text_5")}
                      </li>
                    )}
                    {(getHintColor(powdHints.noRepeatingChars) !== "green" ||
                      getHintColor(powdHints.notStartWithNumber) !== "green" ||
                      getHintColor(powdHints.notEndWithNumber) !== "green" ||
                      getHintColor(powdHints.noSequentialChars) !== "green" ||
                      (hasPBC &&
                        getHintColor(powdHints.noSpecialChars) !== "green") ||
                      getHintColor(powdHints.noForbiddenKeywords) !==
                        "green") && (
                      <li
                        className="hintText no-select"
                        onCopy={(e) => e.preventDefault()}
                      >
                        <Icon
                          alt="alt value"
                          icon="info_outline"
                          size="small"
                          style={{ width: "15px" }}
                        />
                        {t("changePassword.hint_text_7")}
                      </li>
                    )}
                    {(getHintColor(powdHints.noRepeatingChars) !== "green" ||
                      getHintColor(powdHints.notStartWithNumber) !== "green" ||
                      getHintColor(powdHints.notEndWithNumber) !== "green" ||
                      getHintColor(powdHints.noSequentialChars) !== "green" ||
                      (hasPBC &&
                        getHintColor(powdHints.noSpecialChars) !== "green") ||
                      getHintColor(powdHints.noForbiddenKeywords) !==
                        "green") && (
                      <li
                        className="hintText no-select"
                        onCopy={(e) => e.preventDefault()}
                      >
                        <Icon
                          alt="alt value"
                          icon="info_outline"
                          size="small"
                          style={{ width: "15px" }}
                        />
                        {t("changePassword.hint_text11")}
                      </li>
                    )}
                  </div>
                </div>
              )}

              {errors.confirmPassword && (
                <div className="idx-password-error">
                  <Icon icon="error" size="small" />
                  {errors.confirmPassword?.message}
                </div>
              )}

              {password &&
                confirmPassword &&
                password === confirmPassword &&
                isPasswordValid && (
                  <div className="passwordSuccess">
                    <Icon
                      icon="check_circle"
                      size="small"
                      style={{
                        color: "#166A15",
                      }}
                    />

                    <div>
                      <div className="passwordSuccessHeading">
                        {t("changePassword.ready_to_go")}
                      </div>

                      <div className="passwordSuccessParagraph">
                        {t("changePassword.password_meet_requirements")}
                      </div>
                    </div>
                  </div>
                )}

              <Checkbox
                className="idx-terms-checkbox"
                checked={isConfirmed}
                onUpdate={(value) => setIsConfirmed(value)}
                text={
                  <span style={{ marginTop: "2px" }}>
                    {t("changePassword.terms_check")}{" "}
                    <a
                      href="https://myidm.mypepsico.com/iam/im/pepsicopub/page/jsp/legalTermsConditions.jsp"
                      target="_blank"
                      rel="noreferrer"
                    >
                      {t("footer_terms")}
                    </a>
                  </span>
                }
              />

              <div className="idx-password-footer">
                <Button
                  variant="secondary"
                  type="button"
                  onClick={() => navigate(-1)}
                >
                  {t("changePassword.cancel")}
                </Button>

                <Button type="submit" disabled={!isReady || isSubmitting}>
                  {t("changePassword.change_password")}
                </Button>
              </div>
            </form>
          </div>
        </div>
        <ToastContainer position="bottom-center" />
      </div>
    </>
  );
};

export default IdxPasswordReset;
